import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, Button, Box, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Add } from '@mui/icons-material';
import { EditableGrid } from '../../components/DataTable/EditableGrid';
import { ActionCell } from '../../components/DataTable/customcell/ActionCell';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store';
import { validatePricePeriodsGridData } from "../Modals/action-bill-modal/util";
import { fetchSeedsUnitsDropdownPeriods } from '../../slices/productcostapplication.slice';
import { saveSeedsCostData } from "../../slices/productcostseeds.slice";
import moment from "moment";
import { SelectChangeEvent } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useAddCost } from '../../hooks/useAddCost';
import { SeedCostT } from '../../types/addCost';
import type { KeyboardEvent } from "react";
interface Period {
  id: any;
  price: Number;
  discountedPrice: Number;
  seedsPerBag: string;
  notes: string;
  firstSeeded: Date;
  lastSeeded: Date;
}
interface SeedVariety {
  id: number;
  cropName: string;
  variety: string;
  discountedPrice: number;
  perSeed: number;
  bagBushel: string;
  perBushel: number | null;
  periods: Period[];
  priceUnit: string;
}
interface EditModalProps {
  isOpen: boolean;
  editItem: SeedVariety | null;
  handleClose: () => void;
  handleSave: (updatedItem: SeedVariety) => void;
  onItemChange: (e: any) => void;
  deletePricePeriod: (dataItem: any) => void;
  tableData: any[];
  edit: EditDescriptor;
  isCreate: boolean;
}

const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  editItem,
  handleClose,
  isCreate,
}) => {
  const dispatch = useAppDispatch();
  const { handleAddSeedCost } = useAddCost();
  const { seedsCostUnits, cropTypesArray } = useAppSelector(
    (state: RootState) => state.applicationdropdown
  );
  const [selectedUnit, setSelectedUnit] = useState<string>(
    editItem?.priceUnit || ""
  );
  const [selectedCropName, setSelectedCropName] = useState<string>(editItem?.cropName || "");
  const [varietyName, setVarietyName] = useState<string>(editItem?.variety || "");
  const [edit, setEdit] = useState<EditDescriptor>({});
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [periodsData, setPeriodsData] = useState<any[]>(
    editItem?.periods || []
  );
  const varietyInputRef = useRef<HTMLInputElement | null>(null);
  const lastFocusedFieldRef = useRef<"variety" | null>(null);
  useEffect(() => {
    dispatch(fetchSeedsUnitsDropdownPeriods({}));
  }, [dispatch]);

  const handleUnitChange = (event: SelectChangeEvent<string>) => {
    setSelectedUnit(event.target.value as string);
  };

  const handleCropNameChange = (event: SelectChangeEvent<string>) => {
    setSelectedCropName(event.target.value as string);
  };

  const findCropIdByName = (cropName: string): number | undefined => {
    const crop = cropTypesArray.find(
      (cropType) => cropType.cropName === cropName
    );
    return crop ? crop.id : undefined;
  };

  const handleSaveClick = async () => {
    if (editItem) {
      const cropName = isCreate ? selectedCropName : editItem.cropName;
      const variety = isCreate ? varietyName : editItem.variety;
      const cropId = findCropIdByName(cropName);
      const formattedPeriods = periodsData.map((item: any) => {
        return {
          firstSeeded: moment(item.firstSeeded).format("MM/DD/YYYY"),
          lastSeeded: moment(item.lastSeeded).format("MM/DD/YYYY"),
          price: Number(item.price) || 0,
          discountedPrice: Number(item.discountedPrice) || 0,
          seedsPerBag: item.seedsPerBag,
          seedCostPerBushell: Number(item.seedCostPerBushell ?? item.perBushel ?? 0),
          seedCostPerSpeed: Number(item.seedCostPerSpeed ?? item.perSeed ?? 0),
          notes: item.notes,
        };
      });

      if (isCreate) {
        if (!cropId || !selectedUnit || !variety.trim()) {
          return;
        }

        const cropYear = periodsData.length
          ? moment(periodsData[0].firstSeeded).year()
          : new Date().getFullYear();

        const createPayload: SeedCostT = {
          cropId,
          variety: variety.trim(),
          priceUnit: selectedUnit,
          cropYear,
          periods: formattedPeriods,
        };

        const isSuccess = await handleAddSeedCost(createPayload);
        if (isSuccess) {
          handleClose();
        }
        return;
      }

      const { cropName: _cropName, ...updatedItemWithoutCropName } = editItem;
      const updatedItem = {
        ...updatedItemWithoutCropName,
        variety,
        priceUnit: selectedUnit,
        periods: formattedPeriods,
        cropId: cropId,
      };
      dispatch(saveSeedsCostData(updatedItem)).then(handleClose);
    }
  };

  useEffect(() => {
    setErrorFields(
      validatePricePeriodsGridData(periodsData, {
        price: "Price",
        firstSeeded: "First Seeded",
        discountedPrice: "Discounted Price",
        lastSeeded: "Last Seeded",
        type: "Type",
        seedsPerBag: "Seeds Per Bag",
      })
    );
    setEdit(
      periodsData.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {})
    );
  }, [periodsData]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (editItem) {
      setSelectedCropName(editItem.cropName || "");
      setVarietyName(editItem.variety || "");
      setSelectedUnit(editItem.priceUnit || "");
      setPeriodsData(
        editItem.periods.map((item: any) => {
          return {
            ...item,
            id: uuidv4(),
            firstSeeded: moment(item.firstSeeded, "MM/DD/YYYY").toDate(),
            lastSeeded: moment(item.lastSeeded, "MM/DD/YYYY").toDate(),
          };
        })
      );
    } else {
      setSelectedCropName("");
      setVarietyName("");
      setSelectedUnit("");
      setPeriodsData([]);
    }
  }, [isOpen, isCreate, editItem?.id]);

  const canSave = !!(
    periodsData.length &&
    !errorFields.length &&
    selectedUnit &&
    varietyName.trim() &&
    (!isCreate || selectedCropName)
  );

  useLayoutEffect(() => {
    if (!isOpen || !canSave) {
      return;
    }

    if (document.activeElement === document.body && lastFocusedFieldRef.current === "variety") {
      varietyInputRef.current?.focus();
    }
  }, [isOpen, canSave]);

  const addNewPeriod = () => {
    const newPeriod: Period = {
      id: uuidv4(),
      price: 0,
      discountedPrice: 0,
      seedsPerBag: "",
      notes: "",
      firstSeeded: new Date(),
      lastSeeded: new Date(),
    };
    setPeriodsData((prevData) => [...prevData, newPeriod]);
  };

  const handleHeaderInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onItemChange1 = useCallback((e: any) => {
    const { field, value, dataItem } = e;
    setPeriodsData((prevData) =>
      prevData.map((item: any) => {
        if (item.id === dataItem.id) {
          const updatedRow = {
            ...item,
          };
          updatedRow[field] = value;
          return updatedRow;
        }
        return item;
      })
    );
  }, []);

  const deletePricePeriod2 = useCallback((dataItem: any) => {
    setPeriodsData((prevData) => prevData.filter((item: any) => item.id !== dataItem.id));
  }, []);

  const gridColumns: any[] = useMemo(
    () => [
      { field: "price", title: "Price", editor: "numeric" },
      {
        field: "discountedPrice",
        title: "Discounted Price",
        editor: "numeric",
      },
      {
        field: "seedsPerBag",
        title: "Seeds Per Bag",
        editor: "text",
      },
      { field: "notes", title: "Notes" },
      {
        field: "firstSeeded",
        title: "First Seeded date",
        editor: "date",
        format: "{0:d}",
      },
      {
        field: "lastSeeded",
        title: "Last Seeded Date",
        editor: "date",
        format: "{0:d}",
      },
      {
        title: "",
        cells: {
          data: (props: any) => (
            <ActionCell {...props} onDelete={deletePricePeriod2} />
          ),
        },
      },
    ],
    [deletePricePeriod2]
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      sx={{ "& .MuiDialog-paper": { width: "60%", maxWidth: "none" } }}
    >
      <DialogTitle>{isCreate ? "Create Seeds Variety" : "Edit Seeds Variety"}</DialogTitle>
      <DialogContent>
        {editItem && (
          <Box display={"flex"} flexDirection={"column"} gap={5}>
            <Box display={"flex"} flexWrap={"wrap"} gap={2}>
              {isCreate ? (
                <FormControl margin="normal" sx={{ minWidth: 250 }}>
                  <InputLabel>Crop Type</InputLabel>
                  <Select
                    value={selectedCropName}
                    onChange={handleCropNameChange}
                    label="Crop Type"
                  >
                    {cropTypesArray.map((cropType) => (
                      <MenuItem key={cropType.id} value={cropType.cropName}>
                        {cropType.cropName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Crop Type"
                  value={editItem.cropName}
                  sx={{ minWidth: 250 }}
                  margin="normal"
                  disabled
                />
              )}
              <TextField
                label="Variety"
                value={varietyName}
                sx={{ minWidth: 250 }}
                margin="normal"
                disabled={!isCreate}
                inputRef={varietyInputRef}
                onFocus={() => {
                  lastFocusedFieldRef.current = "variety";
                }}
                onKeyDown={handleHeaderInputKeyDown}
                onChange={(event) => {
                  setVarietyName(event.target.value);
                  const el = varietyInputRef.current;
                  if (el) {
                    const pos = el.selectionStart;
                    requestAnimationFrame(() => {
                      if (document.activeElement !== el) {
                        el.focus();
                        try { el.setSelectionRange(pos, pos); } catch (_) {}
                      }
                    });
                  }
                }}
              />
              <FormControl margin="normal" sx={{ minWidth: 250 }}>
                <InputLabel> Price Unit </InputLabel>
                <Select
                  value={selectedUnit}
                  onChange={handleUnitChange}
                  label="Price Unit"
                >
                  {seedsCostUnits.map((unit: any) => (
                    <MenuItem key={unit.id} value={unit.unit}>
                      {unit.unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                mb={1}
                alignItems="center"
              >
                <Typography>Price Periods</Typography>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={addNewPeriod}
                >
                  Add Period
                </Button>
              </Box>
              <EditableGrid
                dataItemKey="id"
                onItemChange={onItemChange1}
                edit={edit}
                columns={gridColumns}
                data={periodsData}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                disabled={!canSave}
              >
                Save
              </Button>
              <Button variant="outlined" color="error" onClick={handleClose}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditModal;